import type { CacheWrite, ModelUsage } from "../usage";
import {
  type CacheWriteMeter,
  type CallCost,
  type CostLine,
  type Meter,
  type PriceRow,
  type PriceUnit,
  type QuantitySource,
  type Sku,
  unknownMeters,
} from "./types";

const UNITS: Record<PriceUnit, number> = {
  "1M tokens": 1_000_000,
  "1k uses": 1_000,
  request: 1,
  USD: 1,
};

const WRITE_METERS: Record<number, CacheWriteMeter> = {
  300: "cacheWrite5m",
  1800: "cacheWrite30m",
  3600: "cacheWrite1h",
  43200: "cacheWrite12h",
};

const INPUT_METERS = ["uncachedInput", "uncachedAudioInput"] as const;

interface Metered {
  meter: Meter;
  quantity: number;
}

/**
 * Every meter a call used, with how much, priced at its SKU's rows. A SKU that offers one cache
 * lifetime keeps every write for it, whatever the request asked; otherwise a write takes the
 * lifetime the response or request names, or the split the router's prompt cost pins down. A
 * router's `auto` SKU prices only the dollars the router reported: see `reportedCostLines`. Usage
 * that went unreported is one `usage` line of unknown quantity; a meter with no row is a line with
 * no price.
 */
export function priceCall(
  sku: Sku | undefined,
  receivedAt: Date,
  usage: ModelUsage | undefined,
  quantitySource: QuantitySource,
): CallCost {
  if (!usage) return costOf(sku, [unknownUsage()]);
  if (sku && isReportedCostSku(sku))
    return costOf(sku, reportedCostLines(sku, receivedAt, usage, quantitySource));
  const promptTokens = usage.inputTokens;
  const rowOf = (meter: Meter) => sku && priceRowFor(sku, meter, receivedAt, promptTokens);
  const metered = [
    ...meteredInput(usage),
    ...usage.cacheWrites.flatMap(write => meteredWrite(write, usage, rowOf)),
    { meter: "output" as const, quantity: usage.outputTokens },
    ...Object.entries(usage.toolUses).map(([tool, uses]) => ({
      meter: `tool:${tool}` as const,
      quantity: uses,
    })),
  ];
  const lines = mergedByMeter(metered)
    .filter(({ quantity }) => quantity > 0)
    .map(({ meter, quantity }) => lineFor(sku, rowOf(meter), meter, quantity, quantitySource));
  return costOf(sku, lines);
}

/**
 * The most a call can cost at its SKU: every prompt token at the priciest input row its band
 * allows, cache writes included, and every output token it may ask for.
 */
export function priceCeiling(
  sku: Sku,
  receivedAt: Date,
  bound: { promptTokens: number; maxOutputTokens: number },
): number | undefined {
  const rowOf = (meter: Meter) => priceRowFor(sku, meter, receivedAt, bound.promptTokens);
  const inputRates = [...INPUT_METERS, ...Object.values(WRITE_METERS)]
    .map(rowOf)
    .flatMap(row => (row ? [row.amount] : []));
  const output = rowOf("output");
  if (inputRates.length === 0 || !output) return;
  return (
    (bound.promptTokens * Math.max(...inputRates) + bound.maxOutputTokens * output.amount) /
    UNITS["1M tokens"]
  );
}

/** Rows of one SKU that would both price the same meter for some call. */
export function overlappingRows(sku: Sku): [PriceRow, PriceRow][] {
  const overlaps: [PriceRow, PriceRow][] = [];
  sku.prices.forEach((row, index) => {
    for (const other of sku.prices.slice(index + 1)) {
      if (
        row.meter === other.meter &&
        datesOverlap(row.effective, other.effective) &&
        windowsOverlap(row.window, other.window) &&
        bandsOverlap(row.promptTokens, other.promptTokens)
      )
        overlaps.push([row, other]);
    }
  });
  return overlaps;
}

/** The row of `sku` that prices `meter` for a call received at `at` with a prompt this long. */
function priceRowFor(sku: Sku, meter: Meter, at: Date, promptTokens: number): PriceRow | undefined {
  return sku.prices.find(
    row =>
      row.meter === meter &&
      withinDates(row.effective, at) &&
      withinWindow(row.window, at) &&
      withinBand(row.promptTokens, promptTokens),
  );
}

/** Prompt tokens neither read from nor written to a cache, then the reads. */
function meteredInput(usage: ModelUsage): Metered[] {
  const written = usage.cacheWrites.reduce((sum, write) => sum + write.tokens, 0);
  const uncached = Math.max(
    0,
    usage.inputTokens - usage.implicitCacheReadTokens - usage.explicitCacheReadTokens - written,
  );
  const uncachedAudio = Math.min(usage.audioInputTokens, uncached);
  return [
    { meter: "uncachedInput", quantity: uncached - uncachedAudio },
    { meter: "uncachedAudioInput", quantity: uncachedAudio },
    { meter: "implicitCacheRead", quantity: usage.implicitCacheReadTokens },
    { meter: "explicitCacheRead", quantity: usage.explicitCacheReadTokens },
  ];
}

/** A cache write on the meter for its lifetime, as far as anything settles it. */
function meteredWrite(
  write: CacheWrite,
  usage: ModelUsage,
  rowOf: (meter: Meter) => PriceRow | undefined,
): Metered[] {
  const offered = Object.values(WRITE_METERS).filter(meter => rowOf(meter));
  if (offered.length === 1 && offered[0]) return [{ meter: offered[0], quantity: write.tokens }];
  const known = write.ttlSeconds === undefined ? undefined : WRITE_METERS[write.ttlSeconds];
  if (known) return [{ meter: known, quantity: write.tokens }];
  return (
    splitByPromptCost(write, usage, rowOf) ?? [
      { meter: "cacheWriteUnknownLifetime", quantity: write.tokens },
    ]
  );
}

/**
 * Writes that may be either of two lifetimes, split by the one mix that makes the prompt's priced
 * lines add up to the prompt cost the router reported. Nothing when there is no such mix.
 */
function splitByPromptCost(
  write: CacheWrite,
  usage: ModelUsage,
  rowOf: (meter: Meter) => PriceRow | undefined,
): Metered[] | undefined {
  const [shorter, longer] = (write.possibleTtlSeconds ?? []).map(seconds => WRITE_METERS[seconds]);
  const prompt = usage.reportedPromptCostUsd;
  if (write.possibleTtlSeconds?.length !== 2 || !shorter || !longer || prompt === undefined) return;
  const shorterRate = rowOf(shorter)?.amount;
  const longerRate = rowOf(longer)?.amount;
  if (shorterRate === undefined || longerRate === undefined || shorterRate === longerRate) return;
  let known = 0;
  for (const { meter, quantity } of meteredInput(usage)) {
    if (quantity === 0) continue;
    const row = rowOf(meter);
    if (!row) return;
    known += (quantity * row.amount) / UNITS[row.unit];
  }
  const tokens = write.tokens;
  const perMillion = UNITS["1M tokens"];
  const shorterTokens = Math.round(
    (known + (tokens * longerRate) / perMillion - prompt) /
      ((longerRate - shorterRate) / perMillion),
  );
  if (shorterTokens < 0 || shorterTokens > tokens) return;
  const total =
    known + (shorterTokens * shorterRate + (tokens - shorterTokens) * longerRate) / perMillion;
  if (Math.abs(total - prompt) > 1e-6 * Math.max(prompt, 1e-3)) return;
  return [
    { meter: shorter, quantity: shorterTokens },
    { meter: longer, quantity: tokens - shorterTokens },
  ];
}

function mergedByMeter(metered: Metered[]): Metered[] {
  const totals = new Map<Meter, number>();
  for (const { meter, quantity } of metered) totals.set(meter, (totals.get(meter) ?? 0) + quantity);
  return [...totals].map(([meter, quantity]) => ({ meter, quantity }));
}

function lineFor(
  sku: Sku | undefined,
  row: PriceRow | undefined,
  meter: Meter,
  quantity: number,
  quantitySource: QuantitySource,
): CostLine {
  if (!sku || !row)
    return {
      meter,
      quantity,
      quantitySource,
      priceId: null,
      unitPriceUsd: null,
      listCostUsd: null,
    };
  return {
    meter,
    quantity,
    quantitySource,
    priceId: `${sku.id}#${row.id}`,
    unitPriceUsd: row.amount,
    listCostUsd: (quantity * row.amount) / UNITS[row.unit],
  };
}

/** A router's `auto` SKU, which prices only the dollars the router reports. */
export function isReportedCostSku(sku: Sku): boolean {
  return sku.prices.every(row => REPORTED_COST_METERS.has(row.meter));
}

const REPORTED_COST_METERS: ReadonlySet<Meter> = new Set([
  "reportedInputCost",
  "reportedOutputCost",
  "reportedCost",
]);

/**
 * The router's reported dollars, split into input and output in proportion to what it says its
 * upstream charged for each, so the two lines add up to exactly what it billed. When it doesn't say,
 * the dollars are one `reportedCost` line; when it reported none, that line's cost is unknown.
 */
function reportedCostLines(
  sku: Sku,
  receivedAt: Date,
  usage: ModelUsage,
  quantitySource: QuantitySource,
): CostLine[] {
  const billed = usage.reportedCostUsd;
  const line = (meter: Meter, usd: number) =>
    lineFor(
      sku,
      priceRowFor(sku, meter, receivedAt, usage.inputTokens),
      meter,
      usd,
      quantitySource,
    );
  if (billed === undefined) return [{ ...unknownUsage(), meter: "reportedCost" }];
  const prompt = usage.reportedPromptCostUsd;
  const completion = usage.reportedCompletionCostUsd;
  if (prompt === undefined || completion === undefined || prompt + completion === 0)
    return [line("reportedCost", billed)];
  const input = (billed * prompt) / (prompt + completion);
  return [line("reportedInputCost", input), line("reportedOutputCost", billed - input)];
}

const unknownUsage = (): CostLine => ({
  meter: "usage",
  quantity: null,
  quantitySource: "unknown",
  priceId: null,
  unitPriceUsd: null,
  listCostUsd: null,
});

const isUnknownMeter = (meter: Meter) => (unknownMeters as readonly Meter[]).includes(meter);

function costOf(sku: Sku | undefined, lines: CostLine[]): CallCost {
  const unknownLines = lines.filter(
    line => line.quantity === null || isUnknownMeter(line.meter),
  ).length;
  const unpricedLines = lines.filter(
    line => line.quantity !== null && !isUnknownMeter(line.meter) && line.priceId === null,
  ).length;
  return {
    sku: sku?.id ?? null,
    lines,
    listCostUsd:
      unknownLines + unpricedLines > 0
        ? null
        : lines.reduce((sum, line) => sum + (line.listCostUsd ?? 0), 0),
    unknownLines,
    unpricedLines,
  };
}

function withinDates(effective: PriceRow["effective"], at: Date): boolean {
  const day = at.toISOString().slice(0, 10);
  return (
    (!effective?.from || day >= effective.from) && (!effective?.until || day < effective.until)
  );
}

function withinWindow(window: PriceRow["window"], at: Date): boolean {
  if (!window) return true;
  const minute = at.getUTCHours() * 60 + at.getUTCMinutes();
  const from = minuteOfDay(window.from);
  const to = minuteOfDay(window.to);
  return from <= to ? minute >= from && minute < to : minute >= from || minute < to;
}

function withinBand(band: PriceRow["promptTokens"], tokens: number): boolean {
  return (
    (band?.over === undefined || tokens > band.over) &&
    (band?.upTo === undefined || tokens <= band.upTo)
  );
}

function datesOverlap(left: PriceRow["effective"], right: PriceRow["effective"]): boolean {
  const start = (range: PriceRow["effective"]) => range?.from ?? "0000-00-00";
  const end = (range: PriceRow["effective"]) => range?.until ?? "9999-99-99";
  return start(left) < end(right) && start(right) < end(left);
}

function windowsOverlap(left: PriceRow["window"], right: PriceRow["window"]): boolean {
  if (!left || !right) return true;
  for (let minute = 0; minute < 24 * 60; minute++) {
    const at = new Date(Date.UTC(2000, 0, 1, 0, minute));
    if (withinWindow(left, at) && withinWindow(right, at)) return true;
  }
  return false;
}

function bandsOverlap(left: PriceRow["promptTokens"], right: PriceRow["promptTokens"]): boolean {
  const low = (band: PriceRow["promptTokens"]) => band?.over ?? -1;
  const high = (band: PriceRow["promptTokens"]) => band?.upTo ?? Number.POSITIVE_INFINITY;
  return low(left) < high(right) && low(right) < high(left);
}

function minuteOfDay(time: string): number {
  const [hours = 0, minutes = 0] = time.split(":").map(Number);
  return hours * 60 + minutes;
}
